<template>
    <div class="shadow-sm sm:overflow-hidden sm:rounded-md">
        <div class="space-y-6 bg-white px-4 py-6 sm:p-6">
            <div>
                <h3 class="text-base font-semibold text-gray-900">{{ $t('gallery_settings.statistics.title') }}</h3>
                <p class="mt-1 text-sm text-gray-500">{{ $t('gallery_settings.statistics.tip_text') }}</p>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="rounded-lg bg-gray-50 ring-1 ring-gray-200 p-8">
                <div class="flex justify-center items-center">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-royal-blue-600"></div>
                    <span class="ml-3 text-sm font-medium text-gray-700">{{ $t('common.loading') }}</span>
                </div>
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="rounded-lg bg-red-50 ring-1 ring-red-200 p-6">
                <div class="flex items-start">
                    <div class="flex-shrink-0">
                        <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clip-rule="evenodd" />
                        </svg>
                    </div>
                    <div class="ml-3 flex-1">
                        <h3 class="text-sm font-medium text-red-800">{{ $t('common.error_loading_data') }}</h3>
                        <div class="mt-3">
                            <button 
                                @click="loadStats" 
                                class="inline-flex justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 cursor-pointer"
                            >
                                {{ $t('common.retry') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Stats Grid -->
            <div v-else-if="stats">
                <div :class="[
                    'grid gap-2 xl:gap-4',
                    gallery.config.stats_enabled 
                        ? 'grid-cols-2 lg:grid-cols-2' 
                        : 'grid-cols-2'
                ]">
                    <!-- Views Card -->
                    <div class="relative overflow-hidden p-4 flex flex-col bg-white border border-gray-200 rounded-xl before:absolute before:top-0 before:end-0 before:size-full before:bg-gradient-to-br before:from-blue-100 before:via-transparent before:blur-xl">
                        <div class="relative z-10">
                            <div class="flex justify-between items-center mb-1">
                                <h2 class="text-4xl font-semibold text-gray-800">
                                    {{ stats.views_count || 0 }}
                                </h2>
                                <div class="flex items-center -space-x-2">
                                    <span class="flex shrink-0 justify-center items-center size-7 bg-white border border-gray-200 text-blue-500 shadow-xs rounded-full">
                                        <svg class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                            <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                            <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                                        </svg>
                                    </span>
                                </div>
                            </div>
                            <h3 class="text-gray-500">
                                {{ $t('gallery_settings.statistics.views') }}
                            </h3>
                        </div>
                    </div>
                    <!-- End Views Card -->

                    <!-- Downloads Card -->
                    <div class="relative overflow-hidden p-4 flex flex-col bg-white border border-gray-200 rounded-xl before:absolute before:top-0 before:end-0 before:size-full before:bg-gradient-to-br before:from-green-100 before:via-transparent before:blur-xl">
                        <div class="relative z-10">
                            <div class="flex justify-between items-center mb-1">
                                <h2 class="text-4xl font-semibold text-gray-800">
                                    {{ stats.downloads_count || 0 }}
                                </h2>
                                <div class="flex items-center -space-x-2">
                                    <span class="flex shrink-0 justify-center items-center size-7 bg-white border border-gray-200 text-green-500 shadow-xs rounded-full">
                                        <svg class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                            <path d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"></path>
                                        </svg>
                                    </span>
                                </div>
                            </div>
                            <h3 class="text-gray-500">
                                {{ $t('gallery_settings.statistics.downloads') }}
                            </h3>
                        </div>
                    </div>
                    <!-- End Downloads Card -->

                    <!-- Files Downloads Card -->
                    <div v-if="gallery.config.stats_enabled" class="relative overflow-hidden p-4 flex flex-col bg-white border border-gray-200 rounded-xl before:absolute before:top-0 before:end-0 before:size-full before:bg-gradient-to-br before:from-purple-100 before:via-transparent before:blur-xl">
                        <div class="relative z-10">
                            <div class="flex justify-between items-center mb-1">
                                <h2 class="text-4xl font-semibold text-gray-800">
                                    {{ stats.files_downloads_count || 0 }}
                                </h2>
                                <div class="flex items-center -space-x-2">
                                    <span class="flex shrink-0 justify-center items-center size-7 bg-white border border-gray-200 text-purple-500 shadow-xs rounded-full">
                                        <svg class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                            <path d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                        </svg>
                                    </span>
                                </div>
                            </div>
                            <h3 class="text-gray-500">
                                {{ $t('gallery_settings.statistics.files_downloads') }}
                            </h3>
                        </div>
                    </div>
                    <!-- End Files Downloads Card -->

                    <!-- Shares Card -->
                    <div v-if="gallery.config.stats_enabled" class="relative overflow-hidden p-4 flex flex-col bg-white border border-gray-200 rounded-xl before:absolute before:top-0 before:end-0 before:size-full before:bg-gradient-to-br before:from-orange-100 before:via-transparent before:blur-xl">
                        <div class="relative z-10">
                            <div class="flex justify-between items-center mb-1">
                                <h2 class="text-4xl font-semibold text-gray-800">
                                    {{ stats.shares_count || 0 }}
                                </h2>
                                <div class="flex items-center -space-x-2">
                                    <span class="flex shrink-0 justify-center items-center size-7 bg-white border border-gray-200 text-orange-500 shadow-xs rounded-full">
                                        <svg class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                            <path d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"></path>
                                        </svg>
                                    </span>
                                </div>
                            </div>
                            <h3 class="text-gray-500">
                                {{ $t('gallery_settings.statistics.shares') }}
                            </h3>
                        </div>
                    </div>
                    <!-- End Shares Card -->
                </div>
                <!-- End Stats Grid -->

                <!-- Last Download Date Section -->
                <div v-if="gallery.config.stats_enabled" class="mt-6">
                    <!-- Card -->
                    <div class="h-full relative flex flex-col bg-white border border-gray-200 rounded-xl">
                        <!-- Body -->
                        <div class="p-1.5 flex flex-col h-full">
                            <div class="p-2.5">
                                <h4 class="font-semibold text-xl md:text-2xl text-gray-800">
                                    {{ stats.last_download_date || $t('gallery_settings.statistics.not_downloaded') }}
                                </h4>
                                <p class="mt-1 text-sm text-gray-600">
                                    {{ $t('gallery_settings.statistics.last_download_date') }}
                                </p>
                            </div>
                        </div>
                        <!-- End Body -->
                    </div>
                    <!-- End Card -->
                </div>
                <!-- End Last Download Date Section -->

                <!-- Downloaded Files Sections -->
                <div v-if="gallery.config.stats_enabled" class="mt-6">
                    <!-- Combined Grid Layout -->
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        
                        <!-- Last Downloaded Files Card -->
                        <div class="relative flex flex-col bg-white border border-gray-200 rounded-xl">
                            <div class="pt-2.5 px-4">
                                <h2 class="font-semibold text-gray-800">
                                    {{ $t('gallery_settings.statistics.last_downloaded_files') }}
                                </h2>
                            </div>

                            <!-- Body -->
                            <div class="p-1.5 flex flex-col flex-1">
                                <div class="pt-1.5">
                                    <!-- Downloaded Files List -->
                                    <div class="p-2.5 rounded-lg bg-gray-100">
                                        <!-- List Group -->
                                        <ul v-if="stats.last_downloaded_files && stats.last_downloaded_files.length > 0" class="space-y-2">
                                            <!-- Item -->
                                            <li v-for="(file, index) in stats.last_downloaded_files.slice(0, 1)" :key="index" class="flex items-center gap-x-2">
                                                <span class="flex shrink-0 justify-center items-center size-6 bg-white border border-gray-200 text-gray-800 text-xs font-medium uppercase rounded-lg">
                                                    {{ getFileIcon(file) }}
                                                </span>
                                                <div class="grow flex justify-between items-center gap-x-2">
                                                    <span class="text-sm font-medium text-gray-600 truncate">
                                                        {{ file }}
                                                    </span>
                                                </div>
                                            </li>
                                            <!-- End Item -->
                                        </ul>
                                        <!-- End List Group -->

                                        <!-- Empty State -->
                                        <div v-else class="text-center py-4">
                                            <div class="flex flex-col items-center">
                                                <svg class="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                                </svg>
                                                <span class="text-sm text-gray-500">
                                                    {{ $t('gallery_settings.statistics.no_downloaded_files') }}
                                                </span>
                                            </div>
                                        </div>
                                        <!-- End Empty State -->
                                    </div>
                                    <!-- End Downloaded Files List -->
                                </div>
                            </div>
                            <!-- End Body -->

                            <!-- Footer with "View All" button -->
                            <div v-if="stats.last_downloaded_files && stats.last_downloaded_files.length > 0" class="px-4 py-2 border-t border-gray-100">
                                <button
                                    @click="openDownloadedPhotosModal"
                                    class="w-full text-center text-xs text-royal-blue-600 hover:text-royal-blue-700 font-medium py-1 cursor-pointer"
                                >
                                    {{ $t('gallery_settings.statistics.view_all_downloaded') }}
                                </button>
                            </div>
                        </div>
                        <!-- End Last Downloaded Files Card -->

                        <!-- Most Downloaded Photo Card -->
                        <div class="h-full relative flex flex-col bg-white border border-gray-200 rounded-xl">
                            <div class="pt-2.5 px-4">
                                <h2 class="font-semibold text-gray-800">
                                    {{ $t('gallery_settings.statistics.most_downloaded_photo') }}
                                </h2>
                            </div>

                            <!-- Body -->
                            <div class="p-1.5 flex flex-col h-full">
                                <div class="pt-1.5">
                                    <!-- Most Downloaded Photo Preview -->
                                    <div class="p-2.5 rounded-lg bg-gray-100">
                                        <div v-if="stats.most_downloaded_photo" class="flex items-center gap-x-3">
                                            <!-- Photo Preview -->
                                            <div class="flex-shrink-0">
                                                <div class="relative w-16 h-16 rounded-lg overflow-hidden bg-gray-200">
                                                    <img 
                                                        :src="stats.most_downloaded_photo.preview_url" 
                                                        :alt="stats.most_downloaded_photo.filename"
                                                        @error="handleImageError"
                                                        class="w-full h-full object-cover"
                                                    />
                                                </div>
                                            </div>
                                            
                                            <!-- File Info -->
                                            <div class="flex-1 min-w-0">
                                                <div class="flex items-center justify-between gap-x-2">
                                                    <span class="text-sm font-medium text-gray-600 truncate">
                                                        {{ stats.most_downloaded_photo.filename }}
                                                    </span>
                                                    <span class="flex-shrink-0 text-base font-bold text-gray-800">
                                                        {{ stats.most_downloaded_photo.downloads_count }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <!-- Empty State -->
                                        <div v-else class="text-center py-4">
                                            <div class="flex flex-col items-center">
                                                <svg class="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                                                </svg>
                                                <span class="text-sm text-gray-500">
                                                    {{ $t('gallery_settings.statistics.no_most_downloaded_photo') }}
                                                </span>
                                            </div>
                                        </div>
                                        <!-- End Empty State -->
                                    </div>
                                    <!-- End Most Downloaded Photo Preview -->
                                </div>
                            </div>
                            <!-- End Body -->
                        </div>
                        <!-- End Most Downloaded Photo Card -->
                    </div>
                    <!-- End Combined Grid Layout -->
                </div>
                <!-- End Downloaded Files Sections -->
            </div>
            <!-- End Stats Content -->
        </div>
    </div>

    <!-- Downloaded Photos Modal -->
    <Teleport to="body">
        <div v-if="showDownloadedPhotosModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
            <!-- Backdrop -->
            <div class="absolute inset-0 bg-black/50" @click="closeDownloadedPhotosModal"></div>

            <!-- Modal -->
            <div class="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
                <!-- Header -->
                <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h3 class="text-base font-semibold text-gray-900">
                        {{ $t('gallery_settings.statistics.downloaded_photos_title') }}
                    </h3>
                    <button @click="closeDownloadedPhotosModal" class="text-gray-400 hover:text-gray-600 cursor-pointer">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>
                </div>

                <!-- Body -->
                <div class="overflow-y-auto flex-1 px-6 py-4">
                    <!-- Loading -->
                    <div v-if="loadingDownloadedPhotos" class="flex justify-center items-center py-8">
                        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-royal-blue-600"></div>
                        <span class="ml-3 text-sm text-gray-700">{{ $t('common.loading') }}</span>
                    </div>

                    <!-- Empty -->
                    <div v-else-if="!downloadedPhotos.length" class="flex flex-col items-center py-8 text-center">
                        <svg class="w-10 h-10 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                        </svg>
                        <span class="text-sm text-gray-500">{{ $t('gallery_settings.statistics.no_most_downloaded_photo') }}</span>
                    </div>

                    <!-- List -->
                    <ul v-else class="space-y-2">
                        <li v-for="(photo, index) in downloadedPhotos" :key="index" class="flex items-center gap-x-3 p-2.5 rounded-lg hover:bg-gray-50">
                            <!-- Rank -->
                            <span class="flex-shrink-0 w-6 text-center text-xs font-medium text-gray-400">
                                {{ index + 1 }}
                            </span>

                            <!-- Preview -->
                            <div class="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-gray-200">
                                <img
                                    :src="photo.preview_url"
                                    :alt="photo.filename"
                                    @error="handleImageError"
                                    class="w-full h-full object-cover"
                                />
                            </div>

                            <!-- Info -->
                            <div class="flex-1 min-w-0 flex items-center justify-between gap-x-2">
                                <span class="text-sm text-gray-700 truncate">{{ photo.filename }}</span>
                                <span class="flex-shrink-0 text-sm font-semibold text-gray-800 whitespace-nowrap">
                                    {{ photo.downloads_count }}
                                    <span class="text-xs font-normal text-gray-400">{{ $t('gallery_settings.statistics.downloaded_photos_downloads') }}</span>
                                </span>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Gallery, GalleryStats, MostDownloadedPhoto } from '@/types/GalleryType';
import { getStats, getDownloadedPhotos } from '@/data/requests/gallery';

export default defineComponent({
    name: 'GalleryStatisticsComponent',
    
    props: {
        gallery: {
            type: Object as () => Gallery,
            required: true
        }
    },

    data() {
        return {
            stats: null as GalleryStats | null,
            loading: false,
            error: false,
            showDownloadedPhotosModal: false,
            loadingDownloadedPhotos: false,
            downloadedPhotos: [] as MostDownloadedPhoto[],
        };
    },

    async mounted() {
        await this.loadStats();
    },

    methods: {
        async loadStats() {
            this.loading = true;
            this.error = false;
            
            getStats(
                this.gallery.slug,
                (stats: GalleryStats) => {
                    this.stats = stats;
                    this.loading = false;
                },
                (error, data) => {
                    console.error('Error loading gallery stats:', error, data);
                    this.error = true;
                    this.loading = false;
                }
            );
        },


        getFileIcon(fileName: string): string {
            const extension = fileName.split('.').pop()?.toLowerCase();
            
            switch (extension) {
                case 'jpg':
                case 'jpeg':
                case 'png':
                case 'gif':
                case 'webp':
                case 'bmp':
                case 'svg':
                    return '📷';
                case 'mp4':
                case 'avi':
                case 'mov':
                case 'wmv':
                case 'flv':
                case 'webm':
                    return '🎥';
                case 'mp3':
                case 'wav':
                case 'flac':
                case 'aac':
                case 'ogg':
                    return '🎵';
                case 'pdf':
                    return '📄';
                case 'doc':
                case 'docx':
                    return '📝';
                case 'zip':
                case 'rar':
                case '7z':
                case 'tar':
                case 'gz':
                    return '📦';
                default:
                    return '📁';
            }
        },

        handleImageError(event: Event) {
            const target = event.target as HTMLImageElement;
            if (target) {
                target.src = require('@/assets/images/img-error.jpg');
            }
        },

        openDownloadedPhotosModal() {
            this.showDownloadedPhotosModal = true;
            this.loadingDownloadedPhotos = true;
            this.downloadedPhotos = [];

            getDownloadedPhotos(
                this.gallery.slug,
                (photos: MostDownloadedPhoto[]) => {
                    this.downloadedPhotos = photos;
                    this.loadingDownloadedPhotos = false;
                },
                () => {
                    this.loadingDownloadedPhotos = false;
                }
            );
        },

        closeDownloadedPhotosModal() {
            this.showDownloadedPhotosModal = false;
        },
    }
});
</script>